import React, { useState, useEffect } from "react";
import { Box, Button, Card, Typography, Grid, TextField, IconButton, CardMedia } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Delete as DeleteIcon, Image as ImageIcon } from "@mui/icons-material";
import { BannerData } from "types/models/master/MasterApp";
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {  updateBanner } from "redux/actions";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";

interface UploadedImage {
  file?: File;
  previewUrl: string;
}

interface BannerProps {
  Banner?: BannerData;
}
const bannerOptions = [

  { value: "homeBanner", label: "Home Banner" },
  { value: "homeBannerMobile", label: "Home Banner Mobile" },
  { value: "pricePlanPage", label: "Price Plan Page" },
  { value: "pricePlanPageMobile", label: "Price Plan Page Mobile" },
  { value: "pricePlanFooterBanner", label: "Price Plan Footer Banner" },
  { value: "pricePlanFooterBannerMobile", label: "Price Plan Footer Banner Mobile" },
];
const positionOption = [
  { value: "Left", label: "Left" },
  { value: "Right", label: "Right" },
  { value: "Bottom", label: "Bottom" },
]
const positionRequiredBanners = new Set([
  "Account Banners",
  "Speed Test Banners",
  "ThankYou Payment Page",
  "Popup Banners",
]);
const BannerUpdate: React.FC<BannerProps> = ({ Banner }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [fileError, setFileError] = useState(false);
  const validationSchema = Yup.object({
    bannerTitle: Yup.string().required("Banner Title is required"),
    indexNo: Yup.number().required("Index Number is required").typeError("Index Number must be a number"),
    url: Yup.string().required("Banner url is required"),
    status: Yup.boolean().required("Status is required"),
    bannerImage: Yup.mixed().test('required-image', 'Banner Image is required', function () {
      return uploadedImages.length > 0;
    }),
  });

  useEffect(() => {
    if (Banner && Banner.image) {
      setUploadedImages([{ previewUrl: Banner.image }]);
    }
  }, [Banner]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (uploadedImages.length > 0) {
        setFileError(true);
        return;
      }
      const file = files[0];
      const newImage = { file, previewUrl: URL.createObjectURL(file) };
      setUploadedImages([newImage]);
      setFileError(false);
    }
  };

  const handleImageDelete = () => {
    setUploadedImages([]);
    setFileError(false);
  };

  const handleSubmit = async (values: any) => {
    if (uploadedImages.length === 0) {
      setFileError(true);
      return;
    }
    const formData = new FormData();

    const dataPayload: any = {
      bannerType: values.bannerType,
      active: values.status,
      index: values.indexNo,
      title: values.bannerTitle,
      uri: values.url,
      image: Banner?.image || "",
    };
    if (positionRequiredBanners.has(values.bannerType)) {
      dataPayload.position = values.position;
    }
    formData.append("data", JSON.stringify(dataPayload));

    if (uploadedImages[0]?.file) {
      formData.append("image", uploadedImages[0].file);
    }

    try {
      if (Banner) {
        console.log("Updating Banner:", values);
        const response = await dispatch(updateBanner(Banner.id, formData)) as unknown as AxiosResponse<any>;

        if (response.status === 200) {
          navigate(`/master/manageBanner/banners`);
        }
      }
    } catch (error) {
      console.error("Error updating banner:", error);
    }
  };

  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", margin: 2 }}>
        <Formik
          initialValues={{
            bannerType: Banner?.bannerType || "",
            bannerTitle: Banner?.title || "",
            indexNo: Banner?.index || 0,
            url: Banner?.uri || "",
            status: Banner?.active || false,
            position: Banner?.position || (positionOption.length > 0 ? positionOption[0].value : ""),
            bannerImage: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Banner Type"
                    name="bannerType"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.bannerType && !!errors.bannerType}
                    helperText={<ErrorMessage name="bannerType" />}
                  >

                    {bannerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Banner Title"
                    name="bannerTitle"
                    variant="outlined"
                    error={touched.bannerTitle && !!errors.bannerTitle}
                    helperText={<ErrorMessage name="bannerTitle" />}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={positionRequiredBanners.has(values.bannerType) ? 6 : 12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="URL"
                    name="url"
                    variant="outlined"
                    error={touched.url && !!errors.url}
                    helperText={<ErrorMessage name="url" />}
                  />
                </Grid>
                {positionRequiredBanners.has(values.bannerType) && (

                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Position"
                      name="position"
                      select
                      variant="outlined"
                      SelectProps={{ native: true }}
                      error={touched.position && !!errors.position}
                      helperText={<ErrorMessage name="position" />}
                    >
                      <option value="">Select Position</option>
                      {positionOption.map((pos) => (
                        <option key={pos.value} value={pos.value}>
                          {pos.label}
                        </option>
                      ))}
                    </Field>

                  </Grid>
                )}
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Index No"
                    name="indexNo"
                    variant="outlined"
                    error={touched.indexNo && !!errors.indexNo}
                    helperText={<ErrorMessage name="indexNo" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Select Status"
                    name="status"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.status && !!errors.status}
                    helperText={<ErrorMessage name="status" />}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                      setFieldValue("status", e.target.value === "true");
                    }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Field>
                </Grid>
              </Grid>

              <Box mb={12}>
                <Typography variant="body1" gutterBottom>
                  Banner Image <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "left" }}>
                  {uploadedImages.length > 0 && (
                    <Card sx={{ maxWidth: "80px", position: "relative" }}>
                      <CardMedia
                        component="img"
                        image={uploadedImages[0].previewUrl}
                        alt="Preview"
                        sx={{ height: "80px", objectFit: "cover" }}
                      />
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={handleImageDelete}
                        sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  )}
                </Box>

                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    padding: 3,
                    backgroundColor: "#f5f5f5",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    id="image-upload-input"
                  />
                  <label htmlFor="image-upload-input" style={{ cursor: "pointer" }}>
                    <ImageIcon sx={{ fontSize: 40 }} />
                    <Typography variant="body1" color="primary">
                      Click to upload or drag and drop
                    </Typography>
                  </label>
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "right", gap: 2, paddingBottom: 2 }}>
                <Button variant="outlined" color="secondary" onClick={() => navigate("/master/manageBanner/banners")}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {Banner ? "Update Banner" : "Add Banner"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>
    </Box>
  );
};

export default BannerUpdate;

